import { logger } from '../../util/logger'
import Hls from 'hls.js'
import React, { RefObject, forwardRef, useEffect, useRef } from 'react'

interface HlsVideoPlayerProps {
  src: string
  autoPlay?: boolean
  controls?: boolean
  /**
   * Defaults to false, so every existing consumer is unchanged. The featured hover preview
   * sets it: browsers refuse to autoplay video with sound, and a marketing shelf that starts
   * talking at you on hover would be its own bug.
   */
  muted?: boolean
  width?: string | number
  height?: string | number
  poster?: string
  className?: string
  hlsConfig?: any
  _playerRef?: RefObject<HTMLVideoElement>
}

export const HlsVideoPlayer = forwardRef<HTMLVideoElement, HlsVideoPlayerProps>(
  (
    {
      src,
      autoPlay = false,
      controls = true,
      muted = false,
      width = '100%',
      height = 'auto',
      poster,
      className,
      hlsConfig,
      _playerRef,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const hlsRef = useRef<Hls | null>(null)

    // Use the forwarded ref, _playerRef prop, or the internal ref in that order
    const videoElement = (ref as RefObject<HTMLVideoElement>) || _playerRef || videoRef

    useEffect(() => {
      const video = videoElement.current
      if (!video) return

      // Clean up previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          startLevel: 2,
          ...hlsConfig,
        })
        hlsRef.current = hls

        hls.loadSource(src)
        hls.attachMedia(video)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (autoPlay) {
            video.play().catch((error) => logger.error('Video play error:', error))
          }
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          logger.error('HLS error:', data)
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                logger.debug('Fatal network error encountered, try to recover')
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                logger.debug('Fatal media error encountered, try to recover')
                hls.recoverMediaError()
                break
              default:
                logger.error('Fatal error, cannot recover')
                hls.destroy()
                break
            }
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = src
        if (autoPlay) {
          video.play().catch((error) => logger.error('Video play error:', error))
        }
      } else {
        logger.error('HLS is not supported in this browser')
      }

      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy()
          hlsRef.current = null
        }
      }
    }, [src, autoPlay, hlsConfig, videoElement])

    return (
      <video
        ref={videoElement}
        controls={controls}
        muted={muted}
        width={width}
        height={height}
        poster={poster}
        className={className}
        style={{ width, height }}
      />
    )
  },
)

HlsVideoPlayer.displayName = 'HlsVideoPlayer'
